/**
 * Clients Admin — list page (SystemAdmin, or a user granted CanManageClients).
 * Snapshot of NMVTIS retention + TZ per client. Click a row to land on the edit page.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Loader2, RefreshCw, AlertCircle, Plus, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Navigation from '../../components/Navigation';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import { getErrorMessage } from '../../services/apiService';
import {
  listClientsAdmin, ClientAdminResponse,
  createPendingClientAdmin, PendingCompanyResponse,
  deleteClientAdmin,
} from '../../services/clientsAdminService';
import { formatPhone, maskPhoneInput } from '../../utils/validation';
import SetupActivateModal from '../../components/admin/SetupActivateModal';
import NetSuiteCustomerPicker from '../../components/admin/NetSuiteCustomerPicker';

// --------------------------------------------------------------------------
// Clients page
// --------------------------------------------------------------------------

const Clients: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isSystemAdmin = user?.isSystemAdmin === true;
  const canManageClients = isSystemAdmin || user?.canManageClients === true;

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate('/login');
    else if (!canManageClients) navigate('/dashboard');
  }, [user, authLoading, navigate, canManageClients]);

  const [rows, setRows] = useState<ClientAdminResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [filter, setFilter] = useState('');

  // New-client create form (for a call-in client who didn't submit a public request).
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    companyName: '',
    contactName: '',
    contactEmail: '',
    phone: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    netSuiteCustomerId: '',
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  // Once created, feed the pending company straight into Setup & Activate.
  const [onboarding, setOnboarding] = useState<PendingCompanyResponse | null>(null);

  const resetForm = () =>
    setCreateForm({
      companyName: '',
      contactName: '',
      contactEmail: '',
      phone: '',
      streetAddress: '',
      city: '',
      state: '',
      zipCode: '',
      netSuiteCustomerId: '',
    });

  const handleCreate = async () => {
    if (!createForm.companyName.trim()) { setCreateError('Company name is required.'); return; }
    setCreating(true); setCreateError('');
    try {
      const pending = await createPendingClientAdmin({
        companyName: createForm.companyName.trim(),
        contactName: createForm.contactName.trim() || undefined,
        contactEmail: createForm.contactEmail.trim() || undefined,
        phone: createForm.phone.trim() || undefined,
        streetAddress: createForm.streetAddress.trim() || undefined,
        city: createForm.city.trim() || undefined,
        state: createForm.state.trim() || undefined,
        zipCode: createForm.zipCode.trim() || undefined,
        netSuiteCustomerId: createForm.netSuiteCustomerId.trim() || undefined,
      });
      setShowCreate(false);
      resetForm();
      setOnboarding(pending); // open Setup & Activate for the new client
    } catch (err) {
      setCreateError(getErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      setRows(await listClientsAdmin());
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { if (canManageClients) load(); }, [canManageClients, load]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0]">
        <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
      </div>
    );
  }

  const filtered = rows.filter(r =>
    !filter || r.name.toLowerCase().includes(filter.toLowerCase()) || (r.state ?? '').toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-radial from-[#c41e3a]/[0.03] to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-radial from-[#c41e3a]/[0.04] to-transparent rounded-full blur-3xl"></div>
      </div>

      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="flex-1 lg:ml-[220px] pt-28 md:pt-32 px-4 lg:px-8 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-[#0f172a] flex items-center gap-3">
                <Building2 className="w-7 h-7 text-[#c41e3a]" />
                Clients
              </h1>
              <p className="text-[#64748b] mt-1">Organizations and their primary contact. Click a row to edit.</p>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter by name or state…"
                className="px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50 w-56"
              />
              <button
                onClick={load}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-black/[0.08] bg-white text-[#475569] hover:border-[#c41e3a]/50 transition-all"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={() => { setCreateError(''); resetForm(); setShowCreate(true); }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c41e3a] text-white font-medium hover:bg-[#a51830] transition-all"
              >
                <Plus className="w-4 h-4" />
                New client
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-xl border flex items-start space-x-3 bg-red-50 border-red-200">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-black/[0.08] overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#f8fafc] text-[#475569] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">Contact</th>
                    <th className="px-4 py-3 text-left">Phone</th>
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-left">State</th>
                    <th className="px-4 py-3 text-left">Timezone</th>
                    <th className="px-4 py-3 text-center">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr><td colSpan={7} className="text-center py-12 text-[#64748b]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-12 text-[#64748b]">No clients match.</td></tr>
                  ) : (
                    filtered.map((c) => (
                      <tr
                        key={c.id}
                        onClick={() => navigate(`/admin/clients/${c.id}`)}
                        className="border-t border-black/[0.05] hover:bg-[#fafbfc] cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 font-medium text-[#0f172a]">{c.name}</td>
                        <td className="px-4 py-3 text-[#64748b]">{c.contactName ?? '—'}</td>
                        <td className="px-4 py-3 text-[#64748b]">{formatPhone(c.phone) || '—'}</td>
                        <td className="px-4 py-3 text-[#64748b]">{c.contactEmail ?? '—'}</td>
                        <td className="px-4 py-3 text-[#64748b]">{c.state ?? '—'}</td>
                        <td className="px-4 py-3 text-[#64748b] font-mono text-xs">{c.timeZone}</td>
                        <td className="px-4 py-3 text-center">{c.isActive ? '✓' : '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* New-client create form — collects the basics, then opens Setup & Activate. */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto" onClick={() => !creating && setShowCreate(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-bold text-[#0f172a]">New client</h2>
              <button onClick={() => !creating && setShowCreate(false)} className="text-[#94a3b8] hover:text-[#475569]"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-[#64748b] mb-4">For a client who calls in to get started. You'll complete banking and invite users on the next step.</p>

            {createError && <div className="mb-3 p-3 rounded-lg border bg-red-50 border-red-200 text-sm text-red-800">{createError}</div>}

            <div className="space-y-3">
              {/* NetSuite picker — always at the top */}
              <NetSuiteCustomerPicker
                linkedId={createForm.netSuiteCustomerId}
                companyName={createForm.companyName}
                contactEmail={createForm.contactEmail}
                phone={createForm.phone}
                onLink={(id) =>
                  setCreateForm((f) => ({ ...f, netSuiteCustomerId: id ?? '' }))
                }
                onPrefill={(data) =>
                  setCreateForm((f) => ({
                    ...f,
                    companyName: data.companyName ?? f.companyName,
                    contactEmail: data.contactEmail ?? f.contactEmail,
                    phone: data.phone ?? f.phone,
                    streetAddress: data.streetAddress ?? f.streetAddress,
                    city: data.city ?? f.city,
                    state: data.state ?? f.state,
                    zipCode: data.zipCode ?? f.zipCode,
                  }))
                }
              />

              <div>
                <label className="block text-xs uppercase tracking-wider text-[#94a3b8] mb-1">Company name *</label>
                <input value={createForm.companyName} onChange={(e) => setCreateForm((f) => ({ ...f, companyName: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-black/[0.08] text-sm focus:outline-none focus:border-[#c41e3a]/50" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#94a3b8] mb-1">Contact name</label>
                <input value={createForm.contactName} onChange={(e) => setCreateForm((f) => ({ ...f, contactName: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-black/[0.08] text-sm focus:outline-none focus:border-[#c41e3a]/50" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#94a3b8] mb-1">Contact email</label>
                <input type="email" value={createForm.contactEmail} onChange={(e) => setCreateForm((f) => ({ ...f, contactEmail: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-black/[0.08] text-sm focus:outline-none focus:border-[#c41e3a]/50" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#94a3b8] mb-1">Phone</label>
                <input value={createForm.phone} placeholder="(555) 555-0100" onChange={(e) => setCreateForm((f) => ({ ...f, phone: maskPhoneInput(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-lg border border-black/[0.08] text-sm focus:outline-none focus:border-[#c41e3a]/50" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#94a3b8] mb-1">Street address</label>
                <input value={createForm.streetAddress} onChange={(e) => setCreateForm((f) => ({ ...f, streetAddress: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-black/[0.08] text-sm focus:outline-none focus:border-[#c41e3a]/50" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-1">
                  <label className="block text-xs uppercase tracking-wider text-[#94a3b8] mb-1">City</label>
                  <input value={createForm.city} onChange={(e) => setCreateForm((f) => ({ ...f, city: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-black/[0.08] text-sm focus:outline-none focus:border-[#c41e3a]/50" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#94a3b8] mb-1">State</label>
                  <input value={createForm.state} onChange={(e) => setCreateForm((f) => ({ ...f, state: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-black/[0.08] text-sm focus:outline-none focus:border-[#c41e3a]/50" placeholder="CA" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#94a3b8] mb-1">ZIP</label>
                  <input value={createForm.zipCode} onChange={(e) => setCreateForm((f) => ({ ...f, zipCode: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-black/[0.08] text-sm focus:outline-none focus:border-[#c41e3a]/50" />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => { setShowCreate(false); resetForm(); }} disabled={creating}
                className="px-4 py-2 rounded-lg border border-black/[0.08] text-[#475569] text-sm hover:bg-black/[0.03] disabled:opacity-50">Cancel</button>
              <button onClick={handleCreate} disabled={creating || !createForm.companyName.trim()}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a51830] disabled:opacity-50">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create &amp; onboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Setup & Activate the just-created client (same modal as Approvals). */}
      {onboarding && (
        <SetupActivateModal
          company={onboarding}
          onClose={() => {
            // User cancelled without completing onboarding — delete the pending client
            // so it doesn't litter the list as an inactive orphan.
            deleteClientAdmin(onboarding.clientId.toString()).catch(() => {});
            setOnboarding(null);
          }}
          onActivated={() => { setOnboarding(null); load(); }}
        />
      )}

      <Footer />
    </div>
  );
};

export default Clients;
